<template>
  <div class="attachable-category-paginated-overview" v-bind:class="{loaded: loaded}">
    <attachable-move-modal
        v-if="hasAttachableCategory"
        :attachables="moveAttachables"
        :customer-id="customerId"
        :has-attachable-type="hasAttachableType"
        :has-attachable-id="hasAttachableId"
        :category="category"
        :visible="showMoveModal"
        v-on:close="onCloseMoveModal"
        v-on:complete="onMoveModalComplete"></attachable-move-modal>
    <attachable-copy-modal
        v-if="hasAttachableCategory"
        :attachables="copyAttachables"
        :customer-id="customerId"
        :has-attachable-type="hasAttachableType"
        :has-attachable-id="hasAttachableId"
        :category="category"
        :visible="showCopyModal"
        v-on:close="onCloseCopyModal"
        v-on:complete="onCopyModalComplete"></attachable-copy-modal>
    <attachable-category-attachable-upload-modal
        v-if="hasAttachableCategory"
        :attachable-category="attachableCategoryValue"
        :customer-id="customerId"
        :has-attachable-type="hasAttachableType"
        :has-attachable-id="hasAttachableId"
        :visible="showUploadModal"
        v-on:close="onCloseUploadModal"
        v-on:created-document="onCreatedDocument"
        v-on:updated-document="onUpdatedDocument">
    </attachable-category-attachable-upload-modal>
    <div style="padding: 8px">
      <a-dropdown class="pull-right global-actions" :trigger="['click']" placement="bottomRight" v-on:click.stop>
        <a-button type="default" @click.stop="e => e.preventDefault()">
          Acties
          <template v-if="hasSelectedAttachables">
            ({{ selectedAttachablesCount }})
          </template>
          <a-icon type="down"/>
        </a-button>
        <a-menu slot="overlay">
          <a-menu-item key="upload" class="action-upload" @click="onUploadClick">
            <upload-icon></upload-icon>
            Uploaden
          </a-menu-item>
          <a-menu-item key="download-multiple" class="action-download-multiple" @click="onDownloadClick">
            <download-icon></download-icon>
            Downloaden
          </a-menu-item>
          <a-menu-item key="copy" class="action-copy" :disabled="!hasSelection" @click="onCopyClick">
            <content-copy-icon></content-copy-icon>
            Kopieer naar
          </a-menu-item>
          <a-menu-item key="move" class="action-move" :disabled="!hasSelection" @click="onMoveClick">
            <content-cut-icon></content-cut-icon>
            Verplaats naar
          </a-menu-item>
          <a-menu-divider/>
          <a-menu-item key="delete" class="action-delete" :disabled="!hasSelection" @click="onDeleteClick">
            <delete-icon></delete-icon>
            Verwijderen
          </a-menu-item>
        </a-menu>
      </a-dropdown>
      <laravel-pagination
          v-if="displayPagination"
          :data="currentRequestData"
          @pagination-change-page="loadPage"
          style="margin: 0;"
      ></laravel-pagination>

      <template v-if="initializing">
        <Skeleton style="width: 140px; height: 30px; display: inline-block; margin-left: 15px;"/>
      </template>

      <div v-if="hasItems" style="display: inline-block; vertical-align: top; margin-left: 15px;" class="per-page">
        Per pagina
        <a-select v-model="perPage" @change="onPerPageChange" style="width: 70px;">
          <a-select-option :value="10">10</a-select-option>
          <a-select-option :value="15">15</a-select-option>
          <a-select-option :value="30">30</a-select-option>
          <a-select-option :value="60">60</a-select-option>
          <a-select-option :value="100">100</a-select-option>
        </a-select>
      </div>
    </div>
    <template v-if="initializing">
      <Skeleton style="width: calc(100% - 20px); height: 30px; display: block; margin: 5px 10px 15px 10px;"/>
      <div v-for="index in count">
        <Skeleton style="width: 40%; height: 20px; display: inline-block; margin: 0 10px 15px 10px;"/>
        <Skeleton style="width: 20%; height: 20px; display: inline-block; margin-right: 10px;"/>
        <Skeleton style="width: 20%; height: 20px; display: inline-block; margin-right: 10px;"/>
      </div>
    </template>
    <table v-if="hasItems" class="table table-striped">
      <thead>
      <tr>
        <th class="compact half-padding">
          <a-checkbox ref="globalCheckbox" :checked="allSelected" :indeterminate="indeterminate"
                      @change="onSelectAllChanged"></a-checkbox>
        </th>
        <th>Naam</th>
        <th class="compact">
          <order-by value="created_at" :order="order" v-on:change="onOrderByChange">Aangemaakt op</order-by>
        </th>
        <th class="compact">Grootte</th>
        <th class="compact"></th>
        <th class="compact"></th>
      </tr>
      </thead>
      <tbody v-bind:class="{ loading: loading }">
      <AttachableSelectableTableRowComponent
          v-for="attachable in attachables"
          :key="attachable.id"
          :attachable="attachable"
          :publish-documents="publishDocuments"
          v-on:selection-changed="onSelectionChanged"
          v-on:copy="onCopy"
          v-on:move="onMove"
          v-on:download="onDownload"
          v-on:delete="onDelete"
          v-on:update="onUpdate"
      >
      </AttachableSelectableTableRowComponent>
      </tbody>
    </table>
    <div v-if="!hasItems && isLoaded" style="padding: 0 10px 15px;">
      Er staan nog geen documenten in deze map
    </div>
    <div v-if="displayPagination" style="padding: 8px">
      <laravel-pagination
          v-if="displayPagination"
          :data="currentRequestData"
          @pagination-change-page="loadPage"
          style="margin: 0;"
      ></laravel-pagination>
    </div>
    <a style="display:none;" ref="downloadLink"></a>
    <form
        ref="zipForm"
        method="POST"
        action=""
        target="_blank"
        style="display: none;"
    >
      <div ref="zipIdsContainer"></div>
    </form>
  </div>
</template>

<script>
import attachableApi from '~/api/attachable'
import attachableCategoryApi from '~/api/attachable_category'
import store from '../store'
import { mapGetters } from 'vuex'
import { Skeleton } from 'vue-loading-skeleton'
import AttachableSelectableTableRowComponent from '../../attachable/components/SelectableTableRowComponent'

export default {
  name: 'AttachableCategoryPaginatedOverviewComponent',
  props: {
    attachableCategory: {
      type: Object,
      default: null,
    },
    attachableCategoryId: {
      type: [String, Number],
      default: null,
    },
    customerId: {
      type: [String, Number],
      default: null,
    },
    hasAttachableType: {
      type: [String, Number],
      default: null,
      required: false
    },
    hasAttachableId: {
      type: [String, Number],
      default: null,
      required: false
    },
    publishDocuments: {
      type: Boolean,
      default: false,
    },
    count: {
      type: Number,
      default: 1,
    },
  },
  components: {
    AttachableSelectableTableRowComponent,
    Skeleton,
  },
  data () {
    return {
      previousSelectedAttachable: null,
      showMoveModal: false,
      showCopyModal: false,
      showUploadModal: false,
      attachableCategoryValue: null,
      reloadTimeout: null,
      attachables: [],
      moveAttachables: [],
      copyAttachables: [],
      currentPage: null,
      currentRequestData: null,
      allSelected: false,
      loading: true,
      loaded: false,
      page: 1,
      perPage: 15,
      orderBy: 'created_at',
      order: 'desc',
    }
  },
  created () {
    const STORE_KEY = '$_attachable_category'
    // eslint-disable-next-line no-underscore-dangle
    if (!(STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(STORE_KEY, store)
    }

    if (this.attachableCategory === null && this.attachableCategoryId === null) {
      throw new Error('The attachable category must be provided')
    }

    this.initAttachableCategory()
  },
  computed: {
    ...mapGetters({
      selectedAttachables: '$_attachable_category/selectedAttachables',
    }),
    initializing () {
      return !this.loaded && this.loading
    },
    selectedAttachablesCount () {
      return this.selectedAttachables.length
    },
    selectedAttachableIds () {
      return this.selectedAttachables.map(attachable => attachable.id)
    },
    hasAttachableCategory () {
      return this.attachableCategoryValue !== null
    },
    hasAttachableCategoryAttachable () {
      return this.hasAttachableCategory
        ? (this.attachableCategoryValue !== null
          && typeof this.attachableCategoryValue.attachable !== 'undefined'
          && typeof this.attachableCategoryValue.attachable.data !== 'undefined')
        : false
    },
    category () {
      if (this.hasAttachableCategoryAttachable) {
        return this.attachableCategoryValue.attachable.data.parent_category
      }

      return null
    },
    hasSelectedAttachables () {
      return this.selectedAttachablesCount > 0
    },
    indeterminate () {
      return this.allSelected === false
        && this.currentSelectionDiff !== this.attachables.length
        && this.currentSelectionDiff > 0
    },
    currentSelectionDiff () {
      return this.attachables.filter(i => this.selectedAttachables.findIndex(x => x.id === i.id) < 0).length
    },
    displayPagination () {
      return this.isLoaded && this.hasItems && typeof this.currentRequestData.meta !== 'undefined'
    },
    hasSelection () {
      return this.selectedAttachables.length > 0
    },
    isLoaded () {
      return this.currentRequestData !== null
    },
    hasItems () {
      return this.currentTotalItemCount !== null && this.currentTotalItemCount > 0
    },
    currentTotalItemCount () {
      return (
        this.currentRequestData !== null
        && typeof this.currentRequestData.meta !== 'undefined'
        && typeof this.currentRequestData.meta !== null
        && typeof this.currentRequestData.meta.pagination !== 'undefined'
        && typeof this.currentRequestData.meta.pagination !== null
        && typeof this.currentRequestData.meta.pagination.total !== 'undefined'
        && typeof this.currentRequestData.meta.pagination.total !== null
      )
        ? this.currentRequestData.meta.pagination.total
        : null
    },
  },
  methods: {
    initAttachableCategory () {
      if (this.attachableCategory === null && this.attachableCategoryId !== null) {
        attachableCategoryApi.find(
          this.attachableCategoryId,
          [
            'attachable',
            'attachables.child_instance',
            'attachables.parent_instance'
          ],
          this.page,
          this.perPage,
          this.orderBy,
          this.order
        ).then(response => {
          this.attachableCategoryValue = response.data

          if (response.data?.attachables?.data) {
            this.allSelected = false
            this.attachables = response.data?.attachables?.data
            this.currentRequestData = response.data?.attachables
            this.loading = false
            this.loaded = true
            this.currentPage = 1
          } else {
            this.initAttachables()
          }
        })
      } else {
        this.attachableCategoryValue = this.attachableCategory

        this.initAttachables()
      }
    },
    initAttachables () {
      this.loadPage(this.page, true)
    },
    loadPage (page = 1, forceReload = false) {
      if (page === this.currentPage && forceReload !== true) {
        return
      }
      this.loading = true

      attachableApi.getByAttachableCategoryId(
        this.attachableCategoryValue.id,
        page,
        this.perPage,
        this.orderBy,
        this.order,
        ['child_instance', 'parent_instance']
      ).then((response) => {
        this.allSelected = false
        this.attachables = response.data
        this.currentRequestData = response
        this.loading = false
        this.loaded = true
        this.currentPage = page

        if (response?.meta?.pagination?.total !== undefined) {
          this.$emit('count-updated', this.attachableCategoryValue.id, response?.meta?.pagination?.total)
        }
      }).catch(() => {
        this.$notification['error']({
          message: 'Er ging iets mis',
          description: 'Het is niet gelukt om documenten te laden',
        })
      })
    },
    onSelectionChanged (attachable, selected, shiftKey) {
      if (this.loading) {
        return
      }

      if (selected === true) {
        this.selectAttachable(attachable)
      } else {
        this.deselectAttachable(attachable)
      }
    },
    onSelectAllChanged () {
      this.allSelected = !this.allSelected

      for (let i in this.attachables) {
        let attachable = this.attachables[i]

        if (this.allSelected === true && this.selectedAttachables.findIndex(x => x.id === attachable.id) < 0) {
          this.selectAttachable(attachable)
        } else if (this.allSelected === false) {
          this.deselectAttachable(attachable)
        }
      }
    },
    deselectAll () {
      this.$store.dispatch('$_attachable_category/resetSelectedAttachables')
    },
    selectAttachable (attachable, shiftKey = false) {
      this.selectedAttachables.push(attachable)

      if (this.currentSelectionDiff === 0) {
        this.allSelected = true
      }

      if (shiftKey) {
        // TODO: add all attachables to the current selection which are in between the current and previous selected attachable
      }

      this.previousSelectedAttachable = attachable
    },
    deselectAttachable (attachable, shiftKey = false) {
      this.allSelected = false

      this.selectedAttachables.splice(this.selectedAttachables.findIndex(x => x.id === attachable.id), 1)

      if (shiftKey) {
        // TODO: remove all attachables from the current selection which are in between the current and previous selected attachable
      }

      this.previousSelectedAttachable = attachable
    },
    onPerPageChange (value) {
      if (this.currentTotalItemCount !== null) {
        let newTotalPages = Math.ceil(this.currentTotalItemCount / value)

        if (this.currentPage > newTotalPages) {
          this.page = 1
        } else {
          this.page = this.currentPage
        }
      } else {
        this.page = 1
      }

      this.reloadPage()
    },
    onOrderByChange (orderBy, order) {
      this.orderBy = orderBy
      this.order = order
      this.loadPage(this.currentPage, true)
    },
    onDownloadClick () {
      attachableApi
        .generateDownloadZipLink(this.selectedAttachableIds)
        .then(response => {
          this.$refs.zipForm.action = response.data.uri
          // Empty the container
          this.$refs.zipIdsContainer.innerHTML = ''

          // Add hidden input for each id
          this.selectedAttachableIds.forEach(id => {
            const input = document.createElement('input')
            input.type = 'hidden'
            input.name = 'ids[]'
            input.value = id
            this.$refs.zipIdsContainer.appendChild(input)
          })

          this.$refs.zipForm.submit()
        })
    },
    onCopyClick () {
      if (this.hasSelection) {
        this.copyAttachables = this.selectedAttachables
        this.showCopyModal = true
      }
    },
    onMoveClick () {
      if (this.hasSelection) {
        this.moveAttachables = this.selectedAttachables
        this.showMoveModal = true
      }
    },
    onUploadClick () {
      this.showUploadModal = true
    },
    onDeleteClick () {
      if (this.hasSelection) {
        let $self = this,
          selectedAttachableIds = this.selectedAttachableIds
        this.$confirm({
          title: this.selectedAttachablesCount > 1
            ? 'Weet je zeker dat je deze documenten ' + this.selectedAttachablesCount + ' wil verwijderen?'
            : 'Weet je zeker dat je dit document wil verwijderen',
          content: 'De geselecteerde documenten zullen worden verwijderd. Deze actie is niet terug te draaien',
          okText: 'Verwijderen',
          okType: 'danger',
          cancelText: 'Annuleren',
          onOk () {
            return attachableApi.deleteAll(selectedAttachableIds).then(response => {
              $self.deselectAll()
              $self.reloadPage()
            })
          },
          onCancel () {
          },
        })
      }
    },
    onCopy (attachable) {
      this.copyAttachables = [attachable]
      this.showCopyModal = true
    },
    onCloseCopyModal () {
      this.showCopyModal = false
    },
    onCopyModalComplete () {
      this.copyAttachables = []
      this.deselectAll()
      this.page = 1
      this.reloadPage()
    },
    onMove (attachable) {
      this.moveAttachables = [attachable]
      this.showMoveModal = true
    },
    onCloseMoveModal () {
      this.showMoveModal = false
      this.deselectAll()
      this.page = 1
      this.reloadPage()
    },
    onCloseUploadModal () {
      this.showUploadModal = false
    },
    onCreatedDocument () {
      this.reloadPage()
    },
    reloadPage (page = null) {
      if (page === null) {
        this.page = this.currentPage
      } else {
        this.page = page
      }

      if (this.reloadTimeout !== null) {
        clearTimeout(this.reloadTimeout)
      }

      this.reloadTimeout = setTimeout(() => {
        this.loadPage(this.page, true)
      }, 200)
    },
    onMoveModalComplete () {
      this.moveAttachables = []
      this.deselectAll()
      this.page = 1
      this.reloadPage()
    },
    onUpdate (attachable) {
      attachableApi.update(attachable)
    },
    onUpdatedDocument () {
      this.reloadPage()
    },
    onDelete (attachable) {
      let $self = this

      this.$confirm({
        title: 'Weet je zeker dat je dit documenten wil verwijderen?',
        content: 'Het geselecteerde document zal worden verwijderd. Deze actie is niet terug te draaien',
        okText: 'Verwijderen',
        okType: 'danger',
        cancelText: 'Annuleren',
        onOk () {
          return attachableApi.delete(attachable.id).then(response => {
            $self.reloadPage()
          })
        },
        onCancel () {
        },
      })
    },
    onDownload (attachable) {
      attachableApi
        .generateDownloadLink(attachable.id)
        .then(response => {
          const uri = response.data.uri

          // open uri in new tab
          window.open(uri, '_blank')
        })
    },
  },
  mounted () {
  }
}
</script>

<style scoped>

.attachable-category-paginated-overview {
  min-height: 50px;
}

tr th.half-padding {
  padding: 4px;
}

tr.selected {
  background-color: #1890ff1c;
}

table th.compact, table td.compact {
  white-space: nowrap;
  width: 1px !important;
}

table > tbody.loading {
  opacity: 0.5;
}

</style>
